import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import { FaArrowLeft, FaSyncAlt } from 'react-icons/fa';
import AdminNav from '../components/admin/AdminNav';
import Button from '../components/common/Button';
import Heading from '../components/common/Heading';
import Section from '../components/common/Section';
import { fetchAdminProgramStudents } from '../services/registrationService';
import { clearAdminToken, getAdminToken } from '../utils/adminAuth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');

const formatDate = (value) => {
  if (!value) {
    return 'N/A';
  }

  return new Intl.DateTimeFormat('en', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(new Date(value));
};

const formatMoney = (value) => `NLe ${Number(value || 0).toLocaleString('en')}`;

const statusClasses = {
  paid: 'bg-green-50 text-green-700 border-green-200',
  pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  registered: 'bg-gray-50 text-gray-700 border-gray-200'
};

const getPhotoUrl = (path) => {
  if (!path) {
    return null;
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return `${API_ORIGIN}${path}`;
};

const AdminProgramStudentsPage = () => {
  const { programId } = useParams();
  const [program, setProgram] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);

  const loadStudents = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await fetchAdminProgramStudents(programId, getAdminToken());
      setProgram(data.program);
      setStudents(data.students || []);
      setHasLoaded(true);
    } catch (requestError) {
      if (requestError.response?.status === 401) {
        clearAdminToken();
      }
      setError(requestError.response?.data?.message || 'Unable to load students.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  return (
    <>
      <Helmet>
        <title>Program Students - Afrovate Tech Hub</title>
      </Helmet>

      <section className="bg-primary-dark pt-32 pb-16 text-white">
        <div className="container mx-auto px-4">
          <Link to="/admin/programs" className="inline-flex items-center text-secondary font-semibold mb-4">
            <FaArrowLeft className="mr-2" />
            Back to Programs
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{program?.title || 'Program Students'}</h1>
          <p className="text-white text-opacity-90 text-lg">Students registered for this program and their payment status.</p>
        </div>
      </section>

      <Section background="white" padding="lg">
        <AdminNav />

        <div className="flex justify-end mb-6">
          <Button variant="outline-primary" onClick={loadStudents} disabled={loading} icon={<FaSyncAlt className={loading ? 'animate-spin' : ''} />}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
        {error && <p className="text-red-600 mb-6">{error}</p>}

        <div className="flex items-center justify-between mb-4">
          <Heading level={2}>Registered Students</Heading>
          <p className="text-gray-600">{students.length} student{students.length !== 1 ? 's' : ''}</p>
        </div>

        <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Photo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Student</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Payment</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {students.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-10 text-center text-gray-500">
                    {hasLoaded ? 'No students registered for this program.' : 'Loading students.'}
                  </td>
                </tr>
              ) : (
                students.map((student) => {
                  const photoUrl = getPhotoUrl(student.passport_photo_path);

                  return (
                    <tr key={student.id}>
                      <td className="px-4 py-4">
                        {photoUrl ? (
                          <a href={photoUrl} target="_blank" rel="noopener noreferrer">
                            <img src={photoUrl} alt={student.full_name} className="h-14 w-14 rounded-full object-cover border border-gray-200" />
                          </a>
                        ) : (
                          <div className="h-14 w-14 rounded-full bg-gray-100"></div>
                        )}
                      </td>
                      <td className="px-4 py-4 min-w-[220px]">
                        <p className="font-semibold text-primary-dark">{student.full_name}</p>
                        <p className="text-sm font-semibold text-secondary">{student.registration_number}</p>
                      </td>
                      <td className="px-4 py-4 min-w-[140px]">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-semibold uppercase tracking-wide ${statusClasses[student.student_status] || statusClasses.registered}`}>
                          {student.student_status}
                        </span>
                      </td>
                      <td className="px-4 py-4 min-w-[220px]">
                        <p className="text-sm text-gray-700">{student.email}</p>
                        <p className="text-sm text-gray-700">{student.whatsapp_number}</p>
                      </td>
                      <td className="px-4 py-4 min-w-[220px]">
                        {student.payment_reference ? (
                          <>
                            <p className="text-sm text-gray-700">Ref: {student.payment_reference}</p>
                            <p className="text-sm text-gray-700">Amount: {formatMoney(student.payment_amount)}</p>
                            <p className="text-sm text-gray-500">Date: {formatDate(student.payment_created_at)}</p>
                          </>
                        ) : (
                          <p className="text-sm text-gray-500">No course payment yet</p>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </>
  );
};

export default AdminProgramStudentsPage;
